<div class="container">
  <p-blockUI [blocked]="isCurtainBlocked"></p-blockUI>

  <mat-toolbar id="dialog-titlebar" mat-dialog-title color="primary">
    <span *ngIf="bundleId == null">Create Bundle - Select Experiments To Add Below</span>
    <span *ngIf="bundleId != null">Edit Bundle (ID: {{ bundleId }})</span>
  </mat-toolbar>

  <form #theForm="ngForm" [formGroup]="bundleService.form" class="normal-form">
    <div class="bundle-controls-inputs">
      <div class="bundle-controls-column top-left-half">
        <mat-form-field appearance="outline">
          <mat-label>Bundle Name</mat-label>
          <input formControlName="name" matInput data-testid="rb-bundlexp-name-input" required />
          <mat-error>This field is mandatory.</mat-error>
        </mat-form-field>

        <div *ngIf="showClinicalTrial" class="clinical-trial-container">
          <mat-checkbox color="primary" data-testid="rb-bundlexp-clinicaltrial-checkbox" [checked]="isClinicalTrialChecked" [disabled]="disableClinicalTrialCheckBox"
            (change)="toggleClinicalStatus()">
            Clinical Trial
          </mat-checkbox>
          <div *ngIf="isClinicalTrialChecked && !disableClinicalTrialCheckBox" class="clinical-trial-hint" id="ClinicalWarning">
            Cannot change after clicking OK
          </div>
        </div>
        <mat-error *ngIf="bundleService.form.value.experimentIds.length < 1" id="experiment-error">At least one
          experiment must be added.</mat-error>
        <div *ngIf="invalidBundleExperimentIds.length > 0" class="missing-experiment-warning" id="missing-experiment-warning">
          <span>Some experiment IDs referenced by this bundle no longer exist: {{ invalidBundleExperimentIds.join(', ') }}</span>
          <button type="button" mat-stroked-button color="warn" data-testid="rb-bundlexp-remove-missing-button" (click)="removeMissingExperimentIds()">Remove missing IDs</button>
        </div>
      </div>

      <div class="bundle-controls-column top-right-half">
        <mat-table id="selected-experiments-table" data-testid="rb-bundlexp-selected-table" [dataSource]="bundleData" matSort #bundleExperimentsSort="matSort"
          matSortActive="dateCreated" matSortDirection="desc">
          <ng-container matColumnDef="experimentId">
            <mat-header-cell *matHeaderCellDef mat-sort-header>Exp ID</mat-header-cell>
            <mat-cell *matCellDef="let element">{{ element.experimentId | experimentFormat : element.type }}</mat-cell>
          </ng-container>
          <ng-container matColumnDef="name">
            <mat-header-cell *matHeaderCellDef mat-sort-header>Name</mat-header-cell>
            <mat-cell *matCellDef="let element">{{ element.name }}</mat-cell>
          </ng-container>
          <ng-container matColumnDef="protocolNumber">
            <mat-header-cell *matHeaderCellDef mat-sort-header>Protocol</mat-header-cell>
            <mat-cell *matCellDef="let element">{{ element.protocolNumber }}</mat-cell>
          </ng-container>
          <ng-container matColumnDef="studyType">
            <mat-header-cell *matHeaderCellDef mat-sort-header>Study Type</mat-header-cell>
            <mat-cell *matCellDef="let element">
              <ng-container *ngIf="element.studyType != null">
                {{ getStudyTypeName(element.studyType) }}
              </ng-container>
              <ng-container *ngIf="element.studyType == null">
              </ng-container>
            </mat-cell>
          </ng-container>
          <ng-container matColumnDef="assayName">
            <mat-header-cell *matHeaderCellDef mat-sort-header>Assay Name</mat-header-cell>
            <mat-cell *matCellDef="let element">
              <ng-container *ngIf="element.assayNameId == null">
                {{ element.assayName }}
              </ng-container>
              <ng-container *ngIf="element.assayNameId != null">
                {{ getAssayName(element.assayNameId) }}
              </ng-container>
            </mat-cell>
          </ng-container>
          <ng-container matColumnDef="actionsRemove">
            <mat-header-cell *matHeaderCellDef></mat-header-cell>
            <mat-cell *matCellDef="let row">
              <button mat-icon-button data-testid="rb-bundlexp-remove-button" (click)="onDelete(row)" matTooltip="Remove Experiment">
                <mat-icon>delete</mat-icon>
              </button>
            </mat-cell>
          </ng-container>
          <ng-container matColumnDef="loading">
            <mat-footer-cell *matFooterCellDef colspan="6"> Loading data... </mat-footer-cell>
          </ng-container>
          <ng-container matColumnDef="noData">
            <mat-footer-cell *matFooterCellDef colspan="6"> Add one or more experiments from the list below...
            </mat-footer-cell>
          </ng-container>
          <mat-header-row *matHeaderRowDef="selectedDisplayedColumns; sticky: true"></mat-header-row>
          <mat-row *matRowDef="let row; columns: selectedDisplayedColumns"></mat-row>
          <mat-footer-row *matFooterRowDef="['loading']" [ngClass]="{ hide: bundleData != null }"></mat-footer-row>
          <mat-footer-row *matFooterRowDef="['noData']"
            [ngClass]="{ hide: !(bundleData != null && bundleData.data.length == 0) }"></mat-footer-row>
        </mat-table>
      </div>
    </div>

    <div class="bundle-controls-column bottom-half" *ngIf="(featureFlags$ | async)?.EnableDataInsightsPreview">
      <div>
        <mat-checkbox formControlName="isUsedToGenerateReport" data-testid="rb-bundlexp-generatereport-checkbox" [disabled]="!!bundleId" (change)="onChangeReportFlag()">
          Bundle used to generate a report</mat-checkbox>
      </div>
      <div class="report-controls" *ngIf="bundleService.form.controls.isUsedToGenerateReport.value">
        <mat-form-field appearance="outline">
          <mat-label>Study Type</mat-label>
          <mat-select id="study-type" data-testid="rb-bundlexp-studytype-select" formControlName="bundleStudyType" (selectionChange)="onChangeStudyType()">
            <mat-option *ngFor="let option of bundleStudyTypes" [value]="option.id">
              {{ option.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Assay Name</mat-label>
          <mat-select id="assay-name-dropdown" data-testid="rb-bundlexp-assayname-select" formControlName="assayNameId" (selectionChange)="onChangeAssayName()">
            <mat-option *ngFor="let option of assayNames" [value]="option.id" [disabled]="!option.isActive">
              {{ option.labelName }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <mat-divider></mat-divider>
    </div>
  </form>

  <div class="bundle-controls-inputs">
    <div class="bundle-controls-column bottom-half">
      <div class="search-div bottom-half">
        <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
          <mat-label>Search Experiments</mat-label>
          <input matInput type="text" data-testid="rb-bundlexp-search-input" autocomplete="off" [(ngModel)]="searchKey" (ngModelChange)="onChange()" />
          <button *ngIf="searchKey" matSuffix mat-icon-button data-testid="rb-bundlexp-search-clear-button" aria-label="Clear search" (click)="onSearchClear()">
            <mat-icon>close</mat-icon>
          </button>
          <mat-icon matSuffix *ngIf="!searchKey">search</mat-icon>
        </mat-form-field>
      </div>

      <div class="mat-elevation-z1">
        <mat-table [dataSource]="experimentsData" data-testid="rb-bundlexp-experiments-table" matSort #allExperimentsSort="matSort" matSortActive="dateCreated"
          matSortDirection="desc" id="experiments-table" mat-row-no-shrink>
          <ng-container matColumnDef="actionsAdd">
            <mat-header-cell *matHeaderCellDef></mat-header-cell>
            <mat-cell *matCellDef="let row">
              <button mat-icon-button data-testid="rb-bundlexp-add-button" *ngIf="!showAddButton(row)" (click)="onAdd(row)" matTooltip="Add Experiment">
                <mat-icon>add</mat-icon>
              </button>
            </mat-cell>
          </ng-container>
          <ng-container matColumnDef="experimentId">
            <mat-header-cell *matHeaderCellDef mat-sort-header>Exp ID</mat-header-cell>
            <mat-cell *matCellDef="let element">{{ element.experimentId | experimentFormat : element.type }}</mat-cell>
          </ng-container>
          <ng-container matColumnDef="name">
            <mat-header-cell *matHeaderCellDef mat-sort-header>Name</mat-header-cell>
            <mat-cell *matCellDef="let element">{{ element.name }}</mat-cell>
          </ng-container>
          <ng-container matColumnDef="protocolNumber">
            <mat-header-cell *matHeaderCellDef mat-sort-header>Protocol</mat-header-cell>
            <mat-cell *matCellDef="let element">{{ element.protocolNumber }}</mat-cell>
          </ng-container>
          <ng-container matColumnDef="studyType">
            <mat-header-cell *matHeaderCellDef mat-sort-header>Study Type</mat-header-cell>
            <mat-cell *matCellDef="let element">
              <ng-container *ngIf="element.studyType != null">
                {{ getStudyTypeName(element.studyType) }}
              </ng-container>
              <ng-container *ngIf="element.studyType == null">
              </ng-container>
            </mat-cell>
          </ng-container>
          <ng-container matColumnDef="assayName">
            <mat-header-cell *matHeaderCellDef mat-sort-header>Assay Name</mat-header-cell>
            <mat-cell *matCellDef="let element">
              <ng-container *ngIf="element.assayNameId == null">
                {{ element.assayName }}
              </ng-container>
              <ng-container *ngIf="element.assayNameId != null">
                {{ getAssayName(element.assayNameId) }}
              </ng-container>
            </mat-cell>
          </ng-container>
          <ng-container matColumnDef="createdByName">
            <mat-header-cell *matHeaderCellDef mat-sort-header>Created By</mat-header-cell>
            <mat-cell *matCellDef="let element">{{ element.createdByName }}</mat-cell>
          </ng-container>
          <ng-container matColumnDef="device">
            <mat-header-cell *matHeaderCellDef mat-sort-header>Device</mat-header-cell>
            <mat-cell *matCellDef="let element">{{ element.deviceName }}</mat-cell>
          </ng-container>
          <ng-container matColumnDef="dateCreated">
            <mat-header-cell *matHeaderCellDef mat-sort-header>Date Created</mat-header-cell>
            <mat-cell *matCellDef="let element">{{ element.dateCreated | date : 'shortDate' }}</mat-cell>
          </ng-container>
          <ng-container matColumnDef="loading">
            <mat-footer-cell *matFooterCellDef colspan="6"> Loading data... </mat-footer-cell>
          </ng-container>
          <ng-container matColumnDef="noData">
            <mat-footer-cell *matFooterCellDef colspan="6"> No data. </mat-footer-cell>
          </ng-container>
          <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
          <mat-row *matRowDef="let row; columns: displayedColumns" class="mat-row-no-shrink"></mat-row>
          <mat-footer-row *matFooterRowDef="['loading']" [ngClass]="{ hide: experimentsData != null }"></mat-footer-row>
          <mat-footer-row *matFooterRowDef="['noData']"
            [ngClass]="{ hide: !(experimentsData != null && experimentsData.data.length == 0) }"></mat-footer-row>
        </mat-table>
        <mat-paginator #paginatorAllExperiments [pageSizeOptions]="[5]" [pageSize]="5"
          showFirstLastButtons></mat-paginator>
      </div>

      <div class="bundle-action-buttons" mat-dialog-actions>
        <button mat-raised-button color="warn" data-testid="rb-bundlexp-cancel-button" (click)="onCancel()">Cancel</button>
        <button mat-raised-button color="primary" data-testid="rb-bundlexp-ok-button" (click)="onSubmit(bundleService.form.value)"
          [disabled]="isFormDisabled()">Ok</button>
      </div>
    </div>
  </div>
</div>